import yaml from "js-yaml";

import type { SdlBuilderFormValuesType, ServiceType } from "@src/types";

/** Mirrors the api's SDL Reference grammar; anchored, so a value merely containing the spelling is not a reference. */
export const SDL_REFERENCE_PATTERN = /^ac-([a-z]{1,16}):\/\/([A-Za-z_][A-Za-z0-9_]{0,63})$/;

/** The api refuses any value opening with this, whether or not it spells a valid reference. */
export const SDL_REFERENCE_PREFIX = "ac-";

const SECRET_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]{0,63}$/;

const SECRET_REFERENCE_KIND = "secret";

/** Mirrors the names the api mints for env values it sealed without being asked: `s<service>_e<position>`, plus a suffix on a collision. */
const PROTECTED_BY_DEFAULT_NAME = /^s\d+_e\d+(_\d+)?$/;

export const REGISTRY_USERNAME_SECRET_NAME = "REGISTRY_USERNAME";
export const REGISTRY_PASSWORD_SECRET_NAME = "REGISTRY_PASSWORD";

const CREDENTIAL_SECRET_FIELDS = ["username", "password"] as const;

type CredentialSecretField = (typeof CREDENTIAL_SECRET_FIELDS)[number];

/** Typed secret values keyed by the name their SDL reference carries. */
export type SdlSecretValues = Record<string, string>;

/** A secret the SDL references but nothing has a value for yet, located for the user by service and field. */
export interface UnresolvedSdlSecret {
  serviceTitle: string;
  label: string;
  name: string;
  /** A reference the form carried in, which the api may already hold a value for from the create, unlike a name this form just minted. */
  isKeptReference: boolean;
}

export interface ResolvedSdlSecrets {
  /** The reference standing in for each secret slot of the generated SDL, keyed by `envSecretSlotKey`/`credentialSecretSlotKey`. */
  references: ReadonlyMap<string, string>;
  /** Everything typed in this session that a create must seal. */
  values: SdlSecretValues;
  unresolved: UnresolvedSdlSecret[];
}

export interface ResolveSdlSecretsOptions {
  /** Withholds every secret value behind a reference; without it the values stay in the document, as a deployment created without a seal needs. */
  sealSecrets?: boolean;
  /** Names some other holder answers for, such as the deployment being redeployed, so a kept reference to one is not unresolved. */
  heldNames?: Iterable<string>;
}

/** The message naming a secret the SDL references that nothing has a value for, shared by the quote and deploy gates. */
export function unresolvedSecretMessage(secret: UnresolvedSdlSecret): string {
  return `Secret "${secret.label}" in service "${secret.serviceTitle}" needs a value.`;
}

export function isSdlReference(value: string): boolean {
  return SDL_REFERENCE_PATTERN.test(value);
}

export function isReservedSdlValue(value: string): boolean {
  return value.startsWith(SDL_REFERENCE_PREFIX);
}

export function isValidSecretName(name: string): boolean {
  return SECRET_NAME_PATTERN.test(name);
}

export function secretReferenceOf(name: string): string {
  return `${SDL_REFERENCE_PREFIX}${SECRET_REFERENCE_KIND}://${name}`;
}

export function secretNameOf(value: string): string | null {
  const reference = SDL_REFERENCE_PATTERN.exec(value);
  return reference && reference[1] === SECRET_REFERENCE_KIND ? reference[2] : null;
}

export function isProtectedByDefault(value: string): boolean {
  return PROTECTED_BY_DEFAULT_NAME.test(secretNameOf(value) ?? "");
}

export function envSecretSlotKey(serviceIndex: number, envIndex: number): string {
  return `${serviceIndex}/env/${envIndex}`;
}

export function credentialSecretSlotKey(serviceIndex: number, field: CredentialSecretField): string {
  return `${serviceIndex}/credentials/${field}`;
}

/** Names are minted deterministically from the env key and never reuse one a kept reference already stands on, so the same form always yields the same SDL. */
export function resolveSdlSecrets(values: SdlBuilderFormValuesType, options: ResolveSdlSecretsOptions = {}): ResolvedSdlSecrets {
  const taken = namesAlreadyReferencedIn(values.services);
  const held = new Set(options.heldNames ?? []);
  const references = new Map<string, string>();
  const secretValues = new Map<string, string>();
  const unresolved: UnresolvedSdlSecret[] = [];

  function keepReference(slotKey: string, reference: string, location: { serviceTitle: string; label: string }) {
    references.set(slotKey, reference);
    const name = secretNameOf(reference);
    if (name !== null && !held.has(name)) unresolved.push({ ...location, name, isKeptReference: true });
  }

  values.services.forEach((service, serviceIndex) => {
    (service.env ?? []).forEach((variable, envIndex) => {
      if (!options.sealSecrets || !variable.isSecret) return;
      const slotKey = envSecretSlotKey(serviceIndex, envIndex);
      const value = variable.value ?? "";
      const location = { serviceTitle: service.title, label: variable.key };

      if (isSdlReference(value)) {
        keepReference(slotKey, value, location);
        return;
      }

      const name = mintSecretName(variable.key.trim(), taken);
      references.set(slotKey, secretReferenceOf(name));
      if (value === "") {
        unresolved.push({ ...location, name, isKeptReference: false });
      } else {
        secretValues.set(name, value);
      }
    });

    if (!options.sealSecrets || !service.hasCredentials || !service.credentials) return;

    CREDENTIAL_SECRET_FIELDS.forEach(field => {
      const slotKey = credentialSecretSlotKey(serviceIndex, field);
      const value = service.credentials?.[field] ?? "";

      if (isSdlReference(value)) {
        keepReference(slotKey, value, { serviceTitle: service.title, label: `registry ${field}` });
        return;
      }
      if (value === "") return;

      const name = mintSecretName(field === "username" ? REGISTRY_USERNAME_SECRET_NAME : REGISTRY_PASSWORD_SECRET_NAME, taken);
      references.set(slotKey, secretReferenceOf(name));
      secretValues.set(name, value);
    });
  });

  return { references, values: Object.fromEntries(secretValues), unresolved };
}

/** The names of every secret reference an SDL carries in a service's env or registry credentials; none when it does not parse. */
export function secretReferenceNamesIn(sdl: string): Set<string> {
  const names = new Set<string>();

  Object.values(servicesOf(sdl)).forEach(service => {
    if (Array.isArray(service.env)) {
      service.env.forEach(entry => {
        if (typeof entry !== "string") return;
        const separatorAt = entry.indexOf("=");
        if (separatorAt !== -1) addSecretName(names, entry.slice(separatorAt + 1));
      });
    }
    const credentials = service.credentials;
    if (credentials && typeof credentials === "object") {
      CREDENTIAL_SECRET_FIELDS.forEach(field => {
        const value = (credentials as Record<string, unknown>)[field];
        if (typeof value === "string") addSecretName(names, value);
      });
    }
  });

  return names;
}

/** Matches on key or value so a renamed or retyped secret is still caught, but leaves a value the form also holds in the clear, which sealing here would not hide. */
export function withSecretsMarkedLike(values: SdlBuilderFormValuesType, markedServices: ServiceType[]): SdlBuilderFormValuesType {
  const variables = markedServices.flatMap(service => service.env ?? []);
  const secrets = variables.filter(variable => variable.isSecret);
  const secretKeys = new Set(secrets.map(variable => variable.key.trim()));
  const secretValues = new Set(secrets.map(variable => variable.value));
  const plainValues = new Set(variables.filter(variable => !variable.isSecret).map(variable => variable.value));
  const isHeldAsSecret = (variable: { key: string; value?: string }) =>
    !!variable.value && !plainValues.has(variable.value) && (secretKeys.has(variable.key.trim()) || secretValues.has(variable.value));

  return {
    ...values,
    services: values.services.map(service => ({
      ...service,
      env: service.env?.map(variable => (isHeldAsSecret(variable) ? withValueWithheld(variable) : variable))
    }))
  };
}

/** A key no secret name can spell would mint a reference the SDL cannot read back, so its value is dropped rather than sealed. */
function withValueWithheld<T extends { key: string }>(variable: T): T {
  return isValidSecretName(variable.key.trim()) ? { ...variable, isSecret: true } : { ...variable, value: "" };
}

function servicesOf(sdl: string): Record<string, { env?: unknown; credentials?: unknown }> {
  try {
    const document = yaml.load(sdl) as { services?: unknown } | null;
    const services = document?.services;
    if (!isRecord(services)) return {};
    return Object.fromEntries(Object.entries(services).filter(([, service]) => isRecord(service))) as Record<string, { env?: unknown; credentials?: unknown }>;
  } catch {
    return {};
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** Read in full before any name is minted, so a typed secret can never land on a name a kept reference elsewhere still stands on. */
function namesAlreadyReferencedIn(services: ServiceType[]): Set<string> {
  const names = new Set<string>();

  services.forEach(service => {
    (service.env ?? []).forEach(variable => addSecretName(names, variable.value));
    CREDENTIAL_SECRET_FIELDS.forEach(field => addSecretName(names, service.credentials?.[field]));
  });

  return names;
}

function addSecretName(names: Set<string>, value: string | undefined) {
  const name = value === undefined ? null : secretNameOf(value);
  if (name !== null) names.add(name);
}

/** The api refuses a name over this, so a suffix has to displace the tail of a long key rather than extend past it. */
const MAX_SECRET_NAME_LENGTH = 64;

export function mintSecretName(preferred: string, taken: Set<string>): string {
  let candidate = preferred.slice(0, MAX_SECRET_NAME_LENGTH);
  let suffix = 2;

  while (taken.has(candidate)) {
    const marker = `_${suffix}`;
    candidate = `${preferred.slice(0, MAX_SECRET_NAME_LENGTH - marker.length)}${marker}`;
    suffix++;
  }

  taken.add(candidate);
  return candidate;
}
